import { twMerge } from 'tailwind-merge';
import { COLLECTION_VIEWS, type CollectionView } from '@witmetrics/unison-core';
import Title from '@/components/Title';
import NewFileMenuButton, {
  type NewFileMenuButtonProps,
} from '@/components/NewFileMenuButton';
import ExpandableSearch from '@/components/inputs/ExpandableSearch';
import ToggleBlock, { ToggleOption } from '@/components/ToggleBlock';
import { GridIcon, TableIcon } from '@/icons';
import DownloadButton from './DownloadButton';
import { classes } from './utils';

export interface FileCollectionHeaderProps extends NewFileMenuButtonProps {
  isFetchingData: boolean;
  downloadable?: boolean;
  variant?: 'default' | 'aurora';
  fileCollectionId?: number;
  collectionView: CollectionView;
  title?: string;
  search: string;
  onSearch: (search: string) => void;
  onChangeView: (view: CollectionView) => void;
  onViewFileCollection: (fileCollectionId: number) => void;
}

export default function FileCollectionHeader({
  isFetchingData,
  downloadable,
  variant,
  fileCollectionId,
  collectionView,
  title = 'Files',
  search,
  onSearch,
  onChangeView,
  onImportSequence,
  onUploadFiles,
  onUploadFileCollections,
  onNewFileCollection,
  onViewFileCollection,
}: FileCollectionHeaderProps) {
  const isAurora = variant === 'aurora';
  const handleSearchUpdate = (val: string) => onSearch(val);

  return (
    <div className={classes.wrapper}>
      <div className={classes.content}>
        <div
          className={twMerge(
            classes.titlesWrapper,
            fileCollectionId && classes.clickable
          )}
          onClick={() =>
            fileCollectionId && onViewFileCollection(fileCollectionId)
          }>
          <Title
            className={isAurora ? classes.auroraTitle : undefined}
            size="sm">
            {title}
          </Title>
        </div>
        <div
          className={twMerge(
            classes.buttonsWrapper,
            isFetchingData && classes.disabled
          )}>
          <ExpandableSearch
            className={classes.search}
            variant={variant}
            value={search}
            onChange={onSearch}
            onClose={() => handleSearchUpdate('')}
          />
          <NewFileMenuButton
            parentFileCollectionId={fileCollectionId}
            variant={variant}
            onImportSequence={onImportSequence}
            onUploadFiles={onUploadFiles}
            onUploadFileCollections={onUploadFileCollections}
            onNewFileCollection={onNewFileCollection}
          />
          {downloadable && fileCollectionId && (
            <DownloadButton
              className={classes.download}
              fileCollectionId={fileCollectionId}
              variant={variant}
            />
          )}
        </div>
      </div>
      <div className={classes.toggleWrapper}>
        <ToggleBlock<CollectionView>
          className={classes.toggleBlock}
          value={collectionView}
          onChange={onChangeView}>
          <ToggleOption value={COLLECTION_VIEWS.LIST} tooltip="List View">
            <TableIcon className={classes.toggleIcon} />
          </ToggleOption>
          <ToggleOption value={COLLECTION_VIEWS.GRID} tooltip="Grid View">
            <GridIcon className={classes.toggleIcon} />
          </ToggleOption>
        </ToggleBlock>
      </div>
    </div>
  );
}
